import type { MeResponse } from "@/lib/me-query";
import { isIntegratorAdmin } from "@/lib/me-query";
import type { AppDescriptor } from "@/components/AppSwitcher";
import { APPS } from "@/components/AppSwitcher";
import { INTEGRATOR_MODULE_KEYS_HIDDEN_FROM_APP_SWITCHER } from "@hub-modules";

export function allowedModuleKeysFromMe(me: MeResponse["user"]): Set<string> {
  const map = me.integratorSubappAccess;
  if (map && Object.keys(map).length > 0) {
    return new Set(
      Object.entries(map)
        .filter(([, level]) => level === "read" || level === "read_write" || level === "admin")
        .map(([key]) => key),
    );
  }
  /** Older `/api/me` responses only returned this list (no per-module read vs read_write). */
  const listOnlyModules = me.allowedIntegratorSubapps;
  if (listOnlyModules == null || listOnlyModules.length === 0) {
    return new Set();
  }
  return new Set(listOnlyModules);
}

/**
 * Hub module operational write (HTTP mutations, BoM MCP draft tools): integrator administrators always allowed;
 * otherwise requires `read_write` or `admin` on that module in `/api/me` `integratorSubappAccess`. If the map is
 * absent, falls back to `allowedIntegratorSubapps` and treats listed modules as write tier (cannot express read-only
 * without the map).
 */
/** True when the user may call read-only hub APIs for this module (any tier, or integrator administrator). */
export function hubModuleHasAnyAccess(me: MeResponse | undefined, appKey: string): boolean {
  if (!me?.user) {
    return false;
  }
  if (isIntegratorAdmin(me.user)) {
    return true;
  }
  return allowedModuleKeysFromMe(me.user).has(appKey);
}

export function hubModuleAllowsWrite(me: MeResponse | undefined, appKey: string): boolean {
  if (!me?.user) {
    return false;
  }
  if (isIntegratorAdmin(me.user)) {
    return true;
  }
  const map = me.user.integratorSubappAccess;
  if (map && Object.keys(map).length > 0) {
    const level = map[appKey];
    return level === "read_write" || level === "admin";
  }
  return Boolean(me.user.allowedIntegratorSubapps?.includes(appKey));
}

/** Operational write when **any** listed module grants read-write or administrator tier. */
/** Read access when **any** listed module grants any tier. */
export function hubModuleHasAnyAccessAny(me: MeResponse | undefined, appKeys: readonly string[]): boolean {
  return appKeys.some((key) => hubModuleHasAnyAccess(me, key));
}

export function hubModuleAllowsWriteAny(me: MeResponse | undefined, appKeys: readonly string[]): boolean {
  if (!me?.user) {
    return false;
  }
  if (isIntegratorAdmin(me.user)) {
    return true;
  }
  const map = me.user.integratorSubappAccess;
  if (map && Object.keys(map).length > 0) {
    return appKeys.some((key) => {
      const level = map[key];
      return level === "read_write" || level === "admin";
    });
  }
  const flat = me.user.allowedIntegratorSubapps;
  if (flat != null && flat.length > 0) {
    return appKeys.some((key) => flat.includes(key));
  }
  return false;
}

/** Hub settings and module-scoped configuration writes require the administrator tier (or integrator administrator). */
export function hubModuleAllowsAdminSettings(me: MeResponse | undefined, appKey: string): boolean {
  if (!me?.user) {
    return false;
  }
  if (isIntegratorAdmin(me.user)) {
    return true;
  }
  const map = me.user.integratorSubappAccess;
  if (map && Object.keys(map).length > 0) {
    return map[appKey] === "admin";
  }
  return false;
}

/** Module administrator tier when **any** listed module grants `admin`. */
export function hubModuleAllowsAdminSettingsAny(me: MeResponse | undefined, appKeys: readonly string[]): boolean {
  return appKeys.some((key) => hubModuleAllowsAdminSettings(me, key));
}

/**
 * App switcher and route guards: integrator administrators always see every module; everyone else sees modules
 * with at least read access in `/api/me` `integratorSubappAccess`, or the flat `allowedIntegratorSubapps` list when
 * the map is omitted. If neither yields keys, treat as no modules until the profile loads correctly.
 */
export function appsVisibleToUser(me: MeResponse | undefined): AppDescriptor[] {
  if (!me?.user) {
    return [...APPS];
  }
  if (isIntegratorAdmin(me.user)) {
    return [...APPS];
  }
  const set = allowedModuleKeysFromMe(me.user);
  if (set.size === 0) {
    return [];
  }
  return APPS.filter((app) => set.has(app.key));
}

/** App switcher, floor tablet full hub, and default landing — excludes avatar-only modules. */
export function appsVisibleInAppSwitcher(me: MeResponse | undefined): AppDescriptor[] {
  return appsVisibleToUser(me).filter(
    (app) => !INTEGRATOR_MODULE_KEYS_HIDDEN_FROM_APP_SWITCHER.has(app.key),
  );
}

export function firstVisibleAppPath(me: MeResponse | undefined): string {
  const list = appsVisibleInAppSwitcher(me);
  const fallback = APPS.find((app) => !INTEGRATOR_MODULE_KEYS_HIDDEN_FROM_APP_SWITCHER.has(app.key));
  return (list[0] ?? fallback)?.path ?? "/mrp";
}
